import type { ReactNode } from 'react';
import type { RowSelection } from '@/components/ui/hooks/use-row-selection';

// Icons attached to a column header or row action come in two shapes from
// consumers: a bare ReactNode, or an object emitted by `createIconObject*`
// helpers ({ icon, direction }). DataTable falls through `x.icon || x` to
// handle both. `direction` is consulted only in the column header path:
// `'right'` pushes the icon button to the far right of the header (via
// `ml-auto`), grouped with the sort indicator; any other value leaves it
// inline next to the column name. Other consumers (e.g. ActionList) use
// their own `direction` semantics — kept as `string` to avoid coupling.
export type ColumnIcon =
  | ReactNode
  | { icon: ReactNode; direction?: string };

// Default row shape — consumers narrow to a domain type when calling
// DataTable. `id` is optional at the row-shape level; row selection has its
// own stricter `{ id: string }` contract enforced by `useRowSelection`.
export type TableRowData = { id?: string | number } & Record<string, unknown>;

/** A single-column sort: which column, and whether it runs ascending. */
export type SortState = { selector: string; asc: boolean };

export type Columns = {
  name: string;
  selector: string;
  type?: string;
  width?: string;
  isSort?: boolean;
  icon?: ColumnIcon;
  cssClass?: string;
  asc?: boolean | null;
  isBold?: boolean;
  isMandatory?: boolean;
  isSortableByHeader?: boolean;
  componentDataType?: string;
  sticky?: 'left' | 'right';
};

export enum filterTypes {
  EQUALS = 'equals',
}

// Cell payload when `column.type === 'component'`. `data` is the sortable
// scalar surfaced to DataTable's sort logic; `component` is what renders.
export type ComponentData = {
  component: ReactNode;
  data?: string | number | Date | null;
};

export type Filters = {
  selector: string;
  value: string;
  criteria?: filterTypes.EQUALS;
};

export type ActionList = {
  key: string;
  icon: ColumnIcon;
};

export type TableConfiguration<TRow extends TableRowData = TableRowData> = {
  columns: Columns[];
  tableData: TRow[];
  filters: Filters[];
  isLoading: boolean;
  showCheckboxes: boolean;
  selection?: RowSelection;
  actionList?: ActionList[];
  onButtonClick?: (key: string, row: TRow) => void;
  actionBar?: ReactNode;
  totalRowsTitle?: string;
  onSort?: (selector: string, asc: boolean) => void;
  /**
   * Server-side consumers: the sort the server is applying right now, so the
   * header arrow (and the mobile sort menu) reflect it and the next header
   * click toggles from it rather than from "unsorted", e.g. a per-view
   * default sort applied before any click (DV-3122). `null` marks no column.
   * When the prop is omitted, DataTable keeps its own click-driven state.
   */
  sortState?: SortState | null;
  onServerPageChange?: (activeIndex: number, rowsPerPage: number) => void;
  /** Sort to open with (header arrow + the next click's direction), for a list restoring where the user left it. Read once at mount. */
  initialSort?: { selector: string; asc: boolean } | null;
  /** 1-based page the pagination footer opens on. Read once at mount. */
  initialPage?: number;
  /** Page size the pagination footer opens with. Read once at mount. */
  initialPageSize?: number;
  onColumnIconClick?: (column: Columns) => void;
  hideActionBar?: boolean;
  hidePagination?: boolean;
  isServerSide?: boolean;
  totalRows?: number;
  totalAvailableRows?: number;
  /** Rendered when the table has no rows at all (tableData is empty). */
  emptyState?: ReactNode;
  /** Rendered when filters eliminated every row. Falls back to emptyState if omitted. */
  noResultsState?: ReactNode;
  /** Below 1024px, rows render as cards via this renderer instead of a <table>. Opt-in per consumer. */
  renderMobileCard?: (row: TRow) => ReactNode;
};

export type PageStep = { lowerIndex: number; upperIndex: number };

export type PaginationConfiguration = {
  totalRows: number;
  onPageChange: (
    upperIndex: number,
    lowerIndex: number,
    activeIndex: number,
    rowsPerPage: number,
  ) => void;
};

export type CheckBoxState = {
  checked: number;
  unchecked: number;
};
